import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ListDTOResponse, IListColumn } from '@global/types'
import { tempIdGenerator } from '@utils/temp-id-generator'
import { KEY } from './useListQuery'
import { t } from '@lingui/core/macro'

interface ISelection {
  start: { col: number }
}

interface IInsertColumnArgs {
  newColumnName: string
  selection: ISelection[]
}

type ListColumnCopy = Partial<IListColumn> & { precedence: number }

const NEW_COLUMN_DEFAULT_ID = -1
const NEW_COLUMN_DEFAULT_ROW = 0
const NEW_COLUMN_DEFAULT_VALUE = ''

function createEmptyListValues(count: number) {
  return Array.from({ length: count }, () => ({
    id: NEW_COLUMN_DEFAULT_ID,
    row_number: NEW_COLUMN_DEFAULT_ROW,
    value: NEW_COLUMN_DEFAULT_VALUE,
  }))
}

/**
 * Custom hook to manage list items in cache using React Query's query client.
 *
 * @returns {Object} Contains methods to interact with the list data cache.
 *
 * @example
 * const { updateList } = useListCache();
 * updateList(updatedData);
 */
export const useListCache = ({ listId }) => {
  const queryClient = useQueryClient()

  /**
   * Merges the provided dataset data with the current data in the cache
   * and updates the cache with the merged data.
   *
   * @param {Object} updated - The dataset data to be added/merged with the current cache.
   */
  const updateList = useCallback(
    (updatedList: ListDTOResponse) => {
      queryClient.setQueryData([KEY, listId], updatedList)
    },
    [listId],
  )

  const getCacheData = useCallback(
    () => queryClient.getQueryData<ListDTOResponse>([KEY, listId]) ?? ({} as ListDTOResponse),
    [listId],
  )

  const insertColumn = useCallback(
    ({ newColumnName, selection }: IInsertColumnArgs) => {
      const old = getCacheData()
      const selectionStartCol = selection[0]?.start.col

      if (!selectionStartCol) {
        return
      }

      // If the selected column is at the end of the columns with data,
      // set its precedence to the correct position and add it to the list.
      const oldListColumns = old.list_columns || []

      const copy: ListColumnCopy[] = oldListColumns.map(col => ({
        ...col,
        precedence: col.precedence >= selectionStartCol ? col.precedence + 1 : col.precedence,
      }))
      const newColumnEmptyListValues = createEmptyListValues(
        oldListColumns[selectionStartCol]?.list_values?.length ?? 1,
      )

      copy.splice(selectionStartCol, 0, {
        id: tempIdGenerator('lc'),
        precedence: selectionStartCol,
        name: newColumnName || t`Column Name`,
        list_id: old.id,
        list_values: newColumnEmptyListValues,
      } as ListColumnCopy)

      updateList({
        ...old,
        list_columns: copy as IListColumn[],
      })
    },
    [updateList, getCacheData],
  )

  // TODO: It's not clear why list_columns is being updated on afterChange, when
  // insertColumn is doing this. Changes could be from a "paste". Paste chould be detected
  // directly, possibly in "beforeChange" similar to our JS handsontable paste feature.
  // const afterChange = changes => {
  //   if (changes?.length === 0 || searchTerm) return

  //   const newColumnLength = Math.max(...changes.map(change => change[1]))
  //   const copy = structuredClone(old.list_columns)
  //   const copyLength = copy.length // cache length since we're mutating copy and it will change.
  //   const newColumnsNeeded = newColumnLength - copyLength + 1

  //   // add needed columns that don't exist yet
  //   for (let i = 0; i <= newColumnLength - copyLength; i++) {
  //     copy.push({
  //       id: tempIdGenerator('lc'),
  //       name: '',
  //       precedence: i + copyLength,
  //       list_values: [],
  //     })
  //   }

  //   // populate grid with values from changes array
  //   changes.forEach(([row, column, _, newValue], index) => {
  //     // there is no guarantee that row_number == array index, we must search for it
  //     const oldCellIndex = copy[column]?.list_values.findIndex(
  //       it => it?.row_number === row,
  //     )
  //     const oldCell = copy[column]?.list_values[oldCellIndex]

  //     // create the new column if needed
  //     if ((oldCell === undefined || oldCell === null || oldCell === '') && newValue) {
  //       copy[column] = copy[column] || {
  //         id: tempIdGenerator('lc'),
  //         name: '',
  //         precedence: index,
  //         list_values: [],
  //       }
  //     }

  //     // remove the old cell
  //     if (oldCellIndex !== -1) {
  //       copy[column].list_values.splice(oldCellIndex, 1)
  //     }

  //     // add the new cell
  //     copy[column].list_values.push({
  //       id: oldCell?.id || tempIdGenerator('lv'),
  //       row_number: row,
  //       value: newValue || (row === 0 ? '' : newValue),
  //     })
  //   })

  //   const listColumnsWithNewColumn = copyLength
  //     ? copy.map(col => ({
  //         ...col,
  //         list_values: col.list_values.map(value => ({ ...value })),
  //       }))
  //     : [newColumn]

  //   onChange()

  //   updateList({
  //     ...old,
  //     list_columns: listColumnsWithNewColumn,
  //   })
  // }

  /**
   * Renames a column in the list and ensures it has necessary properties.
   *
   * @function renameColumn
   * @param {number} columnIndex - The index of the column to be renamed.
   * @param {string} name - The new name for the column.
   *
   * @description
   * This function performs the following operations:
   * 1. Retrieves the current cache data.
   * 2. Creates a shallow copy of the list columns.
   * 3. Updates the specified column with the new name.
   * 4. Ensures the column has an id (generates one if not present).
   * 5. Ensures the column has a list_values array.
   * 6. Updates the list with the modified column data.
   *
   * @example
   * renameColumn(2, "New Column Name")
   *
   * @returns {void}
   */
  const renameColumn = useCallback(
    (columnIndex, name) => {
      const old = getCacheData()
      const list_columns = [...old.list_columns]

      if (!list_columns[columnIndex]) {
        return
      }

      list_columns[columnIndex] = {
        ...list_columns[columnIndex],
        name,
        id: list_columns[columnIndex]?.id || tempIdGenerator('lc'),
        list_values: list_columns[columnIndex]?.list_values || [],
      }

      updateList({
        ...old,
        list_columns,
      })
    },
    [getCacheData],
  )

  const afterRemoveCol = useCallback(
    (index, amount) => {
      const old = getCacheData()
      const copy = [...old.list_columns]
      copy.splice(index, amount)

      updateList({
        ...old,
        list_columns: copy.map((c, index) => {
          c.precedence = index
          return c
        }),
      })
    },
    [getCacheData],
  )

  return {
    afterRemoveCol,
    getCacheData,
    insertColumn,
    renameColumn,
    updateList,
  }
}
